import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { 
  TextInputField, 
  TextareaField,
  PriceInputField,
  NumberInputField,
  SelectField,
  FormSubmitButton
} from '@/components/forms/FormComponents'
import { createProductSchema, updateProductSchema, type CreateProductData, type UpdateProductData } from '@/lib/form-schemas'
import { toastHelpers } from '@/lib/toast-helpers'
import apiClient from '@/api/client'
import { useCategories } from '@/hooks/useCategories'
import type { Product } from '@/types'
import { useCurrency } from '@/contexts/CurrencyContext'
import { allUnitOptions } from '@/lib/inventory-units'

/** Raw file size cap before readAsDataURL (aligned with productImageUrlSchema in form-schemas) */
const MAX_MENU_PHOTO_FILE_BYTES = 5 * 1024 * 1024
import { currencyInputPrefix, formatMoney } from '@/lib/formatMoney'
import { cn } from '@/lib/utils'
import { ChevronDown, Lock, X } from 'lucide-react'

interface ProductFormProps {
  product?: Product // If provided, we're editing; otherwise creating
  onSuccess?: () => void
  onCancel?: () => void
  mode?: 'create' | 'edit'
}

export function ProductForm({ product, onSuccess, onCancel, mode = 'create' }: ProductFormProps) {
  const { currencyCode } = useCurrency()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const isEditing = mode === 'edit' && product

  // Fetch categories for dropdown
  const { data: categories = [] } = useCategories()

  // Create category options for select field
  const categoryOptions = categories.map(cat => ({
    value: cat.id.toString(),
    label: cat.name
  }))

  // The PCT/HS code a new product should carry, derived from its category.
  //
  // This field is read-only in the UI ("managed by the system"), and it used to
  // be hardcoded to '98017000' — the seed default, which is the FBR services
  // code for MESSES AND HOSTELS, not a food or beverage code at all. Because
  // the form always sent a non-empty value, the backend's category-default
  // lookup in createProduct never fired. That is how 35 products leaked onto it
  // on Karo and 8 on NinetyPlus, the latter filing PKR 23,100 under it before
  // anyone noticed (audit/NINETYPLUS_PCT_2026-08-03.md).
  //
  // Returns '' when the category has no usable default. The field then shows
  // the warning below and submits nothing, so the server decides — never this
  // form inventing a code.
  const pctForCategory = (categoryId: string): string => {
    const cat = categories.find(c => String(c.id) === String(categoryId))
    const code = cat?.default_pct_code?.trim() ?? ''
    return /^[0-9]{8}$/.test(code) ? code : ''
  }


  // Choose the appropriate schema and default values
  const schema = isEditing ? updateProductSchema : createProductSchema
  const existingHasCost = isEditing && (product.projected_cost != null || product.projected_cost_basis === 'percent')
  const defaultValues = isEditing 
    ? {
        id: product.id,
        name: product.name,
        description: product.description || '',
        price: product.price,
        category_id: product.category_id != null ? String(product.category_id) : '',
        image_url: product.image_url || '',
        pct_code: product.pct_code || '98017000',
        fbr_uom: product.fbr_uom ?? '',
        fbr_sale_type: product.fbr_sale_type ?? '',
        fbr_sro_schedule_no: product.fbr_sro_schedule_no ?? '',
        fbr_sro_item_serial_no: product.fbr_sro_item_serial_no ?? '',
        fbr_fed_payable: product.fbr_fed_payable ?? 0,
        projected_cost_basis: existingHasCost ? (product.projected_cost_basis ?? 'amount') : undefined,
        projected_cost: product.projected_cost ?? undefined,
        projected_cost_percent: product.projected_cost_percent ?? undefined,
        kind: product.kind ?? 'stock',
        unit: product.unit || 'each',
        sku: product.sku ?? '',
        barcode: product.barcode ?? '',
        unit_cost: product.unit_cost ?? undefined,
        reorder_level: product.reorder_level ?? undefined,
        supplier_id: product.supplier_id ?? null,
      }
    : {
        name: '',
        description: '',
        price: 0,
        category_id: categories[0]?.id != null ? String(categories[0].id) : '',
        image_url: '',
        is_available: true,
        kind: 'stock' as const,
        unit: 'each',
        sku: '',
        barcode: '',
        supplier_id: null,
        pct_code: pctForCategory(categories[0]?.id != null ? String(categories[0].id) : ''),
        fbr_uom: '',
        fbr_sale_type: '',
        fbr_sro_schedule_no: '',
        fbr_sro_item_serial_no: '',
        fbr_fed_payable: 0,
        projected_cost_basis: undefined,
        projected_cost: undefined,
        projected_cost_percent: undefined,
        tracked_stock_item_id: null,
        stock_deduct_qty: 1,
      }

  const form = useForm<CreateProductData | UpdateProductData>({
    resolver: zodResolver(schema),
    defaultValues,
  })

  const imagePreviewUrl = form.watch('image_url')?.trim() || ''

  // Keep the PCT code following the selected category while creating, so the
  // read-only field shows the code the product will actually be filed under
  // rather than a constant the operator cannot see is wrong.
  //
  // Create only. Editing must never silently re-code an existing product just
  // because someone moved it between categories — that is a classification
  // change and belongs in a reviewed script, not a side effect of a menu tidy.
  const watchedCategoryId = form.watch('category_id')
  const categoriesLoaded = categories.length > 0
  useEffect(() => {
    if (isEditing) return
    if (!categoriesLoaded) return
    const next = pctForCategory(String(watchedCategoryId ?? ''))
    if ((form.getValues('pct_code') ?? '') !== next) {
      form.setValue('pct_code', next, { shouldDirty: false })
    }
    // pctForCategory closes over `categories`; categoriesLoaded gates the first
    // run until they arrive, and the id + length pair covers every later change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedCategoryId, categoriesLoaded, categories.length, isEditing])

  // A category with no usable default is the exact condition that generated the
  // Karo leak — Bakery, The Pudding Co and Shakes were all created with a blank
  // default_pct_code, and all 26 products under them inherited the seed value.
  // Surface it at the point of creation instead of discovering it in an audit.
  const categoryHasNoPctDefault =
    !isEditing && categoriesLoaded && pctForCategory(String(watchedCategoryId ?? '')) === ''

  // Default the costing section open when the product already has a cost set,
  // so editing existing items is one click less. New items start collapsed.
  const [costingExpanded, setCostingExpanded] = useState(!!existingHasCost)
  const watchedKind = form.watch('kind') ?? 'stock'
  // Vendors for the product's inventory record. Served from /admin so admin,
  // manager and the inventory manager can all load it.
  const { data: suppliersRaw = [] } = useQuery({
    queryKey: ['admin-product-suppliers'],
    queryFn: () => apiClient.listSuppliersViaAdmin().then(res => res.data ?? []),
  })
  const supplierOptions = suppliersRaw.map(sp => ({ value: sp.id, label: sp.name }))

  const watchedPrice = form.watch('price') ?? 0
  const watchedBasis = form.watch('projected_cost_basis')
  const watchedCost = form.watch('projected_cost')
  const watchedPercent = form.watch('projected_cost_percent')

  // Live margin preview — derived from current form values so the user sees
  // exactly what will be persisted before saving. Mirrors normalizeProjectedCost
  // on the server so the two stay in sync.
  const livePreview = useMemo(() => {
    if (!watchedBasis) return null
    const price = Number(watchedPrice) || 0
    let cost: number | null = null
    if (watchedBasis === 'amount') {
      cost = watchedCost == null ? null : Number(watchedCost)
    } else if (watchedBasis === 'percent') {
      const pct = watchedPercent == null ? null : Number(watchedPercent)
      cost = pct == null ? null : (price * pct) / 100
    }
    if (cost == null || Number.isNaN(cost)) return null
    const margin = price - cost
    const marginPct = price > 0 ? (margin / price) * 100 : 0
    return { cost, margin, marginPct }
  }, [watchedBasis, watchedPrice, watchedCost, watchedPercent])

  const setBasisMode = (mode: 'amount' | 'percent') => {
    form.setValue('projected_cost_basis', mode, { shouldDirty: true })
    if (mode === 'amount') {
      form.setValue('projected_cost_percent', null, { shouldDirty: true })
    } else {
      form.setValue('projected_cost', null, { shouldDirty: true })
    }
  }

  const clearCosting = () => {
    form.setValue('projected_cost_basis', undefined, { shouldDirty: true })
    form.setValue('projected_cost', null, { shouldDirty: true })
    form.setValue('projected_cost_percent', null, { shouldDirty: true })
  }

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateProductData) => apiClient.createProduct(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-products'] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      toastHelpers.productCreated(form.getValues('name') ?? '')
      form.reset()
      onSuccess?.()
    },
    onError: (error) => {
      toastHelpers.apiError('Create product', error)
    },
  })

  // Update mutation  
  const updateMutation = useMutation({
    mutationFn: (data: UpdateProductData) => apiClient.updateProduct(data.id.toString(), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-products'] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['categories'] })
      toastHelpers.apiSuccess('Update', `Product "${form.getValues('name') ?? ''}"`)
      onSuccess?.()
    },
    onError: (error) => {
      toastHelpers.apiError('Update product', error)
    },
  })

  const onSubmit = (data: CreateProductData | UpdateProductData) => {
    // Submit-time guard for the costing block. The schema is intentionally
    // permissive (so collapsed = untouched works), but if the user opted in
    // by expanding the section they must enter a value before saving.
    if (costingExpanded) {
      if (!data.projected_cost_basis) {
        // Default to amount if not set
        data = { ...data, projected_cost_basis: 'amount' }
      }
      if (data.projected_cost_basis === 'amount' && (data.projected_cost == null || Number.isNaN(Number(data.projected_cost)))) {
        form.setError('projected_cost', { type: 'manual', message: 'Enter a cost amount or switch to "% of price"' })
        return
      }
      if (data.projected_cost_basis === 'percent' && (data.projected_cost_percent == null || Number.isNaN(Number(data.projected_cost_percent)))) {
        form.setError('projected_cost_percent', { type: 'manual', message: 'Enter a percent (0-100)' })
        return
      }
    } else {
      // Section is collapsed → don't touch costing on the server. Drop the
      // trio entirely so the backend's "basis present = update trio" guard
      // doesn't fire.
      const { projected_cost_basis: _b, projected_cost: _c, projected_cost_percent: _p, ...rest } = data
      void _b; void _c; void _p
      data = rest as CreateProductData | UpdateProductData
    }

    // Inventory tracking (migrations/035). When on, at least one stock item
    // must be chosen; each ratio is clamped positive and duplicates are dropped.
    // The first component is mirrored into the legacy scalar fields. When off,
    // send an empty list so editing an existing tracked product un-tracks it.

    // Tags (product_tags) are assigned tag-centrically from Reports → Items →
    // Tag now, not here. We deliberately omit tag_ids so a product edit leaves
    // the product's existing tag membership untouched (updateProduct treats a
    // nil tag_ids as "don't change").

    if (isEditing) {
      updateMutation.mutate(data as UpdateProductData)
    } else {
      createMutation.mutate(data as CreateProductData)
    }
  }

  const removeProjectedCost = () => {
    if (!isEditing) {
      // No persistent cost yet — just collapse the section locally.
      clearCosting()
      setCostingExpanded(false)
      return
    }
    // Tell the backend explicitly to clear the trio. basis='amount' with both
    // value fields null is the canonical "no cost set" state.
    updateMutation.mutate({
      id: product.id,
      projected_cost_basis: 'amount',
      projected_cost: null,
      projected_cost_percent: null,
    } as UpdateProductData)
    clearCosting()
    setCostingExpanded(false)
  }

  const isLoading = createMutation.isPending || updateMutation.isPending

  if (categories.length === 0) {
    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">
              You need to create at least one category before adding products.
            </p>
            <Button onClick={onCancel} variant="outline">
              Go Back
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>
          {isEditing ? 'Edit Product' : 'Create New Product'}
        </CardTitle>
        {onCancel && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onCancel}
            disabled={isLoading}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <TextInputField
                control={form.control}
                name="name"
                label="Product Name"
                placeholder="Enter product name"
                description="The name that will appear in the catalog and on receipts"
              />
              
              <TextareaField
                control={form.control}
                name="description"
                label="Description"
                placeholder="Describe the product..."
                rows={3}
                description="Optional description for staff and customers"
              />

              <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
                <div>
                  <Label className="text-base">Product photo</Label>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Upload has a limit of 5 mb.
                  </p>
                </div>
                <TextInputField
                  control={form.control}
                  name="image_url"
                  label="Image URL"
                  placeholder="https://example.com/image.jpg"
                  description="Optional product image URL"
                />
                <div className="space-y-1.5">
                  <Label htmlFor="product-image-file" className="text-sm font-medium">
                    Or upload an image
                  </Label>
                  <input
                    id="product-image-file"
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                    className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border file:bg-background file:px-3 file:py-1.5 file:text-sm file:font-medium"
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      if (!f) return
                      if (f.size > MAX_MENU_PHOTO_FILE_BYTES) {
                        toastHelpers.error(
                          'Image too large',
                          'Please use a file under 5MB (try exporting or compressing the photo), or paste an HTTPS link instead.'
                        )
                        e.target.value = ''
                        return
                      }
                      const reader = new FileReader()
                      reader.onload = () => {
                        const result = String(reader.result || '')
                        form.setValue('image_url', result, { shouldValidate: true, shouldDirty: true })
                      }
                      reader.readAsDataURL(f)
                    }}
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-xs text-muted-foreground"
                      onClick={() => {
                        form.setValue('image_url', '', { shouldValidate: true, shouldDirty: true })
                        if (fileInputRef.current) fileInputRef.current.value = ''
                      }}
                    >
                      Clear photo
                    </Button>
                  </div>
                  {imagePreviewUrl &&
                    (imagePreviewUrl.startsWith('http') || imagePreviewUrl.startsWith('data:image')) && (
                      <div className="pt-1">
                        <p className="mb-1 text-xs text-muted-foreground">Preview</p>
                        <img
                          src={imagePreviewUrl}
                          alt=""
                          className="h-24 w-24 rounded-md border object-cover"
                        />
                      </div>
                    )}
                </div>
              </div>
            </div>

            {/* Pricing & Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <PriceInputField
                control={form.control}
                name="price"
                label="Price"
                currency={currencyInputPrefix(currencyCode)}
                description="Product selling price"
              />
              
            </div>

            {/* Projected costing & margin (optional) — collapsed by default
                so the form stays light for users who don't track this. */}
            <div className="rounded-lg border bg-muted/30">
              <button
                type="button"
                aria-expanded={costingExpanded}
                aria-controls="product-costing-panel"
                onClick={() => {
                  setCostingExpanded((prev) => {
                    const next = !prev
                    if (next && !form.getValues('projected_cost_basis')) {
                      form.setValue('projected_cost_basis', 'amount', { shouldDirty: true })
                    }
                    return next
                  })
                }}
                className="flex w-full items-start justify-between gap-3 rounded-lg px-4 py-3 text-left hover:bg-muted/50"
              >
                <div className="flex-1 space-y-0.5">
                  <p className="text-sm font-medium">
                    Projected cost &amp; margin{' '}
                    <span className="text-xs font-normal text-muted-foreground">(optional)</span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {existingHasCost && !costingExpanded
                      ? 'Cost is set — click to edit'
                      : 'Add an assumed cost per unit to see this item\'s projected profitability in reports'}
                  </p>
                </div>
                <ChevronDown
                  className={cn(
                    'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
                    costingExpanded && 'rotate-180',
                  )}
                  aria-hidden
                />
              </button>
              {costingExpanded && (
                <div id="product-costing-panel" className="space-y-4 border-t px-4 py-4" role="region">
                  <p className="text-xs text-muted-foreground">
                    This is an <em>assumption</em> — separate from the expenses ledger.
                    Projected gross margin shows up on reports alongside (not instead of) your tracked Net Profit.
                  </p>

                  <div className="space-y-2">
                    <Label className="text-sm">Cost basis</Label>
                    <div
                      role="radiogroup"
                      aria-label="Cost basis"
                      className="inline-flex rounded-md border bg-background p-0.5"
                    >
                      <button
                        type="button"
                        role="radio"
                        aria-checked={watchedBasis !== 'percent'}
                        onClick={() => setBasisMode('amount')}
                        className={cn(
                          'rounded px-3 py-1.5 text-xs font-medium transition-colors',
                          watchedBasis !== 'percent'
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-muted',
                        )}
                      >
                        Amount ({currencyInputPrefix(currencyCode)})
                      </button>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={watchedBasis === 'percent'}
                        onClick={() => setBasisMode('percent')}
                        className={cn(
                          'rounded px-3 py-1.5 text-xs font-medium transition-colors',
                          watchedBasis === 'percent'
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-muted',
                        )}
                      >
                        % of price
                      </button>
                    </div>
                  </div>

                  {watchedBasis === 'percent' ? (
                    <NumberInputField
                      control={form.control}
                      name="projected_cost_percent"
                      label="Cost as % of price"
                      min={0}
                      max={100}
                      description="e.g. 35 means cost is 35% of the sale price"
                    />
                  ) : (
                    <PriceInputField
                      control={form.control}
                      name="projected_cost"
                      label="Cost per unit"
                      currency={currencyInputPrefix(currencyCode)}
                      description="What it costs you to make/serve one of these"
                    />
                  )}

                  {livePreview && watchedPrice > 0 && (
                    <div className="rounded-md border bg-background px-3 py-2.5 text-xs">
                      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                        <span className="text-muted-foreground">Cost</span>
                        <span className="font-medium">{formatMoney(livePreview.cost, currencyCode)}</span>
                        <span className="text-muted-foreground">·</span>
                        <span className="text-muted-foreground">Projected profit</span>
                        <span className={cn('font-semibold', livePreview.margin < 0 && 'text-destructive')}>
                          {formatMoney(livePreview.margin, currencyCode)}
                          {' '}
                          <span className="text-muted-foreground">({livePreview.marginPct.toFixed(1)}%)</span>
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between gap-2 pt-1">
                    <p className="text-xs text-muted-foreground">
                      Tip: use the bulk Costing tab to set costs for many items at once.
                    </p>
                    {existingHasCost && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-destructive hover:text-destructive"
                        onClick={removeProjectedCost}
                        disabled={isLoading}
                      >
                        Remove cost
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Inventory. Every stock product is tracked — there is no opt-out
                — so this section sets up the product's stock record rather than
                asking whether to keep one. Services are the escape hatch for
                things that are sold but never counted. */}
            <div className="rounded-lg border bg-muted/30 p-4 space-y-4">
              <div className="space-y-1">
                <h4 className="text-sm font-semibold">Inventory</h4>
                <p className="text-xs text-muted-foreground">
                  Stock items are counted and deducted on every sale. Choose Service for
                  something you sell but never count — labour, fees, delivery charges.
                </p>
              </div>

              <SelectField
                control={form.control}
                name="kind"
                label="Type"
                options={[
                  { value: 'stock', label: 'Stock item — counted and deducted on sale' },
                  { value: 'service', label: 'Service — never affects stock' },
                ]}
              />

              {watchedKind === 'stock' && (
                <div className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <SelectField
                      control={form.control}
                      name="unit"
                      label="Unit"
                      options={allUnitOptions}
                      description="How you count it"
                    />
                    <NumberInputField
                      control={form.control}
                      name="reorder_level"
                      label="Reorder level"
                      placeholder="0"
                      description="Flags the item as low stock at or below this"
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <TextInputField
                      control={form.control}
                      name="sku"
                      label="SKU"
                      placeholder="Optional"
                      description="Also searchable at the counter"
                    />
                    <TextInputField
                      control={form.control}
                      name="barcode"
                      label="Barcode"
                      placeholder="Scan or type"
                      description="Scanning this at the till adds the product"
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <NumberInputField
                      control={form.control}
                      name="unit_cost"
                      label="Cost price"
                      placeholder="0.00"
                      description="What you pay per unit — drives margin and COGS"
                    />
                    {!isEditing && (
                      <NumberInputField
                        control={form.control}
                        name="opening_stock"
                        label="Opening stock"
                        placeholder="0"
                        description="How many you have right now"
                      />
                    )}
                  </div>

                  <SelectField
                    control={form.control}
                    name="supplier_id"
                    label="Vendor"
                    options={[{ value: '', label: '— None —' }, ...supplierOptions]}
                    placeholder="Optional"
                    description="Who you buy it from"
                  />

                  {isEditing && (
                    <p className="rounded-md bg-background px-3 py-2 text-xs text-muted-foreground">
                      On hand: <span className="font-medium text-foreground">
                        {product?.quantity_on_hand ?? 0} {product?.unit ?? 'each'}
                      </span>
                      {' — '}adjust it from Inventory, not here, so every change leaves a movement record.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Category */}
            <SelectField
              control={form.control}
              name="category_id"
              label="Category"
              options={categoryOptions}
              placeholder="Select a category"
              description="Product category for catalog organization"
            />

            <FormField
              control={form.control}
              name="pct_code"
              render={({ field }) => (
                <FormItem className="rounded-md border border-amber-200 bg-amber-50/60 p-4 dark:border-amber-900/60 dark:bg-amber-950/20">
                  <FormLabel className="flex items-center gap-2">
                    <Lock className="h-3.5 w-3.5 text-amber-700 dark:text-amber-400" aria-hidden />
                    PCT / HS code (FBR/PRA)
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      readOnly
                      aria-readonly
                      className="bg-background/70 cursor-not-allowed"
                      placeholder={categoryHasNoPctDefault ? 'Set on the category first' : '98017000'}
                    />
                  </FormControl>
                  <FormDescription>
                    {isEditing
                      ? 'This code is required for FBR/PRA tax compliance and is managed by the system. Contact support to change it.'
                      : 'Inherited from the selected category. Change it on the category, not here.'}
                  </FormDescription>
                  {categoryHasNoPctDefault && (
                    <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
                      This category has no default HS/PCT code set, so this product will fall back to
                      the system default — which is a services code, not a food or beverage one. Set
                      the category's default before creating items under it, or this product will be
                      filed under the wrong code.
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <FormSubmitButton
                isLoading={isLoading}
                loadingText={isEditing ? "Updating..." : "Creating..."}
                className="flex-1"
              >
                {isEditing ? 'Update Product' : 'Create Product'}
              </FormSubmitButton>
              
              {onCancel && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={isLoading}
                  className="flex-1"
                >
                  Cancel
                </Button>
              )}
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
